import { STATUS } from '@front-app/constants/state';
import { Ref, ref } from '@vue/composition-api';
import { isObject } from '@vueuse/shared';

export interface StoreCacheItem<T> {
	status: Ref<STATUS>;
	item: T;
}

export type StoreCache<K extends string | number | symbol, T> = Record<K, StoreCacheItem<Ref<T>>>;

export type GetDefaultCacheItem<K, T> = (id: K) => T

export const STARTABLE_STATUSES = [
	STATUS.ERROR,
	STATUS.UNSTARTED,
];

export interface UseCache<K, T> {
	get(id: K): T;
	keys(): K[];
	getStatus(id: K): Ref<STATUS>;
	set(id: K, item: T): T;
	setStatus(id: K, status: STATUS): void;
	setWithStatus(id: K, item: T, status: STATUS): T;
	update(id: K, item: Partial<T>): T;
	has(id: K): boolean;
	remove(id: K): void;
	clear(): void;
}

export const useCache = <K extends string | number | symbol, T>(cache: StoreCache<K, T>, getDefault: GetDefaultCacheItem<K, T>): UseCache<K, T> => {
	const innerCache = cache;
	const get = (id: K): T => {
		innerCache[id] = innerCache[id] || newCacheItem(getDefault(id));
		return innerCache[id].item.value;
	};
	const keys = (): K[] => Object.keys(innerCache) as K[];
	const getStatus = (id: K): Ref<STATUS> => {
		innerCache[id] = innerCache[id] || newCacheItem(getDefault(id));
		return innerCache[id].status;
	};
	const set = (id: K, item: T): T => {
		innerCache[id] = innerCache[id] || newCacheItem(getDefault(id));
		if (isObject(innerCache[id].item.value)) {
			innerCache[id].item.value = { ...innerCache[id].item.value, ...item };
		} else {
			innerCache[id].item.value = item;
		}
		return innerCache[id].item.value;
	};
	const setStatus = (id: K, status: STATUS) => {
		innerCache[id] = innerCache[id] || newCacheItem(getDefault(id));
		innerCache[id].status.value = status;
	};
	const setWithStatus = (id: K, item: T, status: STATUS) => {
		set(id, item);
		setStatus(id, status);
		return innerCache[id].item.value;
	};
	const update = (id: K, item: Partial<T>): T => {
		innerCache[id] = innerCache[id] || newCacheItem(getDefault(id));
		innerCache[id].item.value = { ...innerCache[id].item.value, ...item };
		return innerCache[id].item.value;
	};
	const remove = (id: K): void => {
		innerCache[id].item.value = getDefault(id);
		innerCache[id].status.value = STATUS.UNSTARTED;
	};
	const has = (id: K): boolean => Boolean(innerCache[id]);
	const clear = (): void => (Object.keys(innerCache) as K[]).forEach((key) => remove(key));
	return {
		keys,
		get,
		getStatus,
		set,
		setStatus,
		setWithStatus,
		update,
		has,
		remove,
		clear,
	};
};

function newCacheItem<T>(defaultItem: T): StoreCacheItem<Ref<T>> {
	const item = ref(defaultItem) as Ref<T>;
	return {
		status: ref(STATUS.UNSTARTED),
		item,
	};
}
